import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import ThreadList from './components/ThreadList';
import FanArtGrid from './components/FanArtGrid';
import NewThreadModal from './components/NewThreadModal';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import SEOHead from '../../components/feature/SEOHead';

type Board = 'feedback' | 'theory' | 'fanart';
type SortBy = 'latest' | 'most_replies' | 'most_liked';

export default function CommunityPage() {
  const [activeBoard, setActiveBoard] = useState<Board>('feedback');
  const [sortBy, setSortBy] = useState<SortBy>('latest');
  const [showNewThreadModal, setShowNewThreadModal] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [threadCounts, setThreadCounts] = useState({ feedback: 0, theory: 0, fanart: 0 });
  const { user } = useAuth();
  const navigate = useNavigate();

  // ---------------------------------
  // Scroll handling (adds a shadow to navbar after scrolling)
  // ---------------------------------
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ---------------------------------
  // Refresh thread counts when `refreshKey` changes
  // ---------------------------------
  useEffect(() => {
    fetchThreadCounts();
  }, []);

  // ---------------------------------
  // Fetch the exact count of threads per board
  // ---------------------------------
  const fetchThreadCounts = async () => {
    try {
      const { data, error } = await supabase
        .from('forum_threads')
        .select('board');

      if (error) throw error;

      const counts = {
        feedback: data?.filter((t: any) => t.board === 'feedback').length || 0,
        theory: data?.filter((t: any) => t.board === 'theory').length || 0,
        fanart: data?.filter((t: any) => t.board === 'fanart').length || 0,
      };

      setThreadCounts(counts);
    } catch (error) {
      console.error('Error fetching thread counts:', error);
    }
  };

  // ---------------------------------
  // Open modal or redirect to login
  // ---------------------------------
  const handleNewPost = () => {
    if (!user) {
      navigate('/auth/login');
      return;
    }
    setShowNewThreadModal(true);
  };

  // ---------------------------------
  // Trigger a refresh of the thread list and counts
  // ---------------------------------
  const handleThreadCreated = () => {
    fetchThreadCounts();
  };

  const boards = [
    { id: 'feedback' as Board, label: 'Story Feedback', icon: 'ri-chat-heart-line', count: threadCounts.feedback },
    { id: 'theory' as Board, label: 'Theory Crafting', icon: 'ri-question-mark', count: threadCounts.theory },
    { id: 'fanart' as Board, label: 'Fan Art', icon: 'ri-image-line', count: threadCounts.fanart },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <SEOHead
        title="The Dark Congregation – Horror Community Forums | Sinister Files"
        description="Join the Sinister Files horror community. Discuss horror stories, share fan art, debate theories, and connect with fellow fans of dark fiction and creepypasta."
        canonical="https://sinisterfiles.com/community"
      />
      <Navbar />

      {/* Hero Section */}
      <div className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/4818b7353a4abaa8356435834b993069.jpg"
            alt="The Dark Congregation"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-[#0a0a0a]"></div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl">
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-['Cinzel'] text-zinc-100 mb-4">
            The Dark Congregation
          </h1>
          <p className="text-lg md:text-xl text-zinc-300 font-['Crimson_Text'] mb-8 leading-relaxed">
            Gather in the shadows to share feedback, unravel mysteries, and celebrate the art born from darkness
          </p>
          <button
            onClick={handleNewPost}
            className="inline-flex items-center gap-2 px-8 py-3 bg-red-900 hover:bg-red-800 text-zinc-100 font-['Cinzel'] text-sm tracking-wide transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line text-xl"></i>
            New Post
          </button>
        </div>
      </div>

      {/* Board Navigation */}
      <div className={`sticky top-[72px] z-40 bg-[#0a0a0a] border-b border-red-900/20 transition-all ${
        scrolled ? 'shadow-lg shadow-black/50' : ''
      }`}>
        <div className="max-w-7xl mx-auto px-4 md:px-6">

          {/* Mobile: 3-column grid matching modal style */}
          <div className="grid grid-cols-3 gap-2 py-3 md:hidden">
            {boards.map((board) => (
              <button
                key={board.id}
                onClick={() => setActiveBoard(board.id)}
                className={`flex flex-col items-center justify-center gap-1 px-2 py-3 border transition-all cursor-pointer ${
                  activeBoard === board.id
                    ? 'bg-red-900/20 border-red-900 text-red-400'
                    : 'bg-zinc-900/50 border-red-900/20 text-zinc-400 hover:border-red-900/40'
                }`}
              >
                <i className={`${board.icon} text-lg`}></i>
                <span className="text-xs font-['Cinzel'] text-center leading-tight">{board.label}</span>
              </button>
            ))}
          </div>

          {/* Desktop: original horizontal tab bar */}
          <div className="hidden md:flex items-center gap-1">
            {boards.map((board) => (
              <button
                key={board.id}
                onClick={() => setActiveBoard(board.id)}
                className={`flex items-center gap-2 px-6 py-4 border-b-2 transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                  activeBoard === board.id
                    ? 'border-red-900 text-red-400'
                    : 'border-transparent text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <i className={`${board.icon} text-lg`}></i>
                <span className="font-['Cinzel'] text-sm">{board.label}</span>
                <span className="px-2 py-0.5 bg-zinc-900/50 border border-red-900/20 text-xs">
                  {board.count}
                </span>
              </button>
            ))}
          </div>

        </div>
      </div>

      {/* Sort Controls */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h2 className="text-xl md:text-2xl font-['Cinzel'] text-zinc-100">
            {boards.find((b) => b.id === activeBoard)?.label}
          </h2>
          <div className="flex items-center gap-2 overflow-x-auto scrollbar-none pb-1 sm:pb-0">
            <span className="text-sm text-zinc-500 font-['Crimson_Text'] whitespace-nowrap flex-shrink-0">Sort by:</span>
            {[
              { value: 'latest' as SortBy, label: 'Latest' },
              { value: 'most_replies' as SortBy, label: 'Most Replies' },
              { value: 'most_liked' as SortBy, label: 'Most Liked' },
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => setSortBy(option.value)}
                className={`px-3 md:px-4 py-2 text-xs md:text-sm font-['Cinzel'] transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                  sortBy === option.value
                    ? 'bg-red-900/20 border border-red-900 text-red-400'
                    : 'text-zinc-500 hover:text-zinc-300'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Thread List or Fan Art Grid */}
        {activeBoard === 'fanart' ? (
          <FanArtGrid sortBy={sortBy} />
        ) : (
          <ThreadList board={activeBoard} sortBy={sortBy} />
        )}
      </div>

      <Footer />

      {/* New Thread Modal */}
      {showNewThreadModal && (
        <NewThreadModal
          initialBoard={activeBoard}
          onClose={() => setShowNewThreadModal(false)}
          onSuccess={handleThreadCreated}
        />
      )}
    </div>
  );
}
